import {ASK_SOURCES,COACH_LINE_MAX,COACH_OFF_HOURS_CODE,type AskSource,type Call,type Capability,type CoachAskAnswer,type CoachResponse} from '@bcio-voice/contracts';
import {isAbortError,type ApiClient,type DataEnvelope} from '../../lib/api.ts';
import {describeError,type ErrorView} from '../../lib/errorCopy.ts';
import type {MediaAccess} from '../../state/mediaAccess.ts';
import {ASK_HISTORY_LIMIT,cleanQuestion,seconds} from '../../features/transcript/ask.ts';
import {readTranscriptSegment,subscribeTranscriptStream,type TranscriptStreamNotification} from '../../features/transcript/liveTranscript.ts';
import {coachOpening} from './coachOpening.ts';

export const COACH_POLL_MS=10000,COACH_STORAGE_KEY='sidetone.coach.enabled';
export const COACH_EVENT_DEBOUNCE_MS=150,COACH_REQUEST_GAP_MS=2500;
/** The two quick actions that stay on the call window (Allen, October 30, 2026). "Say something funny" is the server's
 * `lighten` action (apps/api/src/coach/prompt.ts maps the words); the button text is sent as the question. */
export const COACH_PRIMARY_ACTIONS=['Break the ice','Say something funny'] as const;
/** The rest, behind one "More" control. */
export const COACH_MORE_ACTIONS=["They're busy",'Handle this objection','Ask one good question','Get to the point','Close with one next step','Reset me'] as const;
/** Live-call situations a rep can tap in one move; each is sent to the coach as the question text. */
export const COACH_QUICK_ACTIONS=[...COACH_PRIMARY_ACTIONS,...COACH_MORE_ACTIONS] as const;
export const COACH_QUICK_QUESTIONS=COACH_QUICK_ACTIONS;
/** Rep-only encouragement: the answer is for the rep, never a line to say to the customer. */
export const isRepOnlyQuickAction=(question:string)=>/\b(?:reset\s+me|quick\s+reset|rep-only\s+reset|rep\s+reset|calm\s+me\s+down|take\s+a\s+breath|ground\s+me|shake\s+it\s+off|rough\s+call)\b/i.test(question);
export const coachVisible=(cap:Capability|null|undefined,permissions:readonly string[],mediaReady:boolean)=>
 !!cap?.allowed&&(cap.status==='enabled'||cap.status==='degraded')&&permissions.includes('transcripts:read')&&mediaReady;
type Storage=Pick<globalThis.Storage,'getItem'|'setItem'>;
export const coachUserKey=(tenantId:string|null|undefined,userId:string)=>JSON.stringify([tenantId??'',userId]);
export function coachPreference(storage:Storage|null,user:string):boolean{
 try{return JSON.parse(storage?.getItem(COACH_STORAGE_KEY)??'{}')[user]===true;}catch{return false;}
}
export function coachDefaultEnabled(storage:Storage|null,user:string):boolean{
 try{
  const parsed=JSON.parse(storage?.getItem(COACH_STORAGE_KEY)??'{}');
  if(parsed&&typeof parsed==='object'&&!Array.isArray(parsed)&&user in parsed){
   return (parsed as Record<string,unknown>)[user]!==false;
  }
  return true;
 }catch{return true;}
}
export function saveCoachPreference(storage:Storage|null,user:string,enabled:boolean){
 try{
  let previous:unknown;try{previous=JSON.parse(storage?.getItem(COACH_STORAGE_KEY)??'{}');}catch{previous={};}
  const values=typeof previous==='object'&&previous!==null&&!Array.isArray(previous)?previous:{};
  storage?.setItem(COACH_STORAGE_KEY,JSON.stringify({...values,[user]:enabled}));
 }catch{/* An unavailable storage does not prevent this call's toggle from working. */}
 for(const listener of [...coachPreferenceListeners])listener(user,enabled);
}
export function coachStorage():Storage|null{try{return typeof window==='undefined'?null:window.localStorage;}catch{return null;}}
/** The dock's Live coach switch and the in-call checkbox are one preference. A save tells every mounted control, so
 * they stay in step even when storage is unavailable. */
type CoachPreferenceListener=(user:string,enabled:boolean)=>void;
const coachPreferenceListeners=new Set<CoachPreferenceListener>();
export function subscribeCoachPreference(listener:CoachPreferenceListener):()=>void{coachPreferenceListeners.add(listener);return()=>{coachPreferenceListeners.delete(listener);};}
export type CoachSwitchCopy={available:boolean;tone:'live'|'neutral'|'warn';state:string;detail:string};
/** Copy for the dock switch. Availability is the account's standing (a configured coach, the transcript grant); the
 * in-call panel still checks media access and the call state itself, so this never flickers with them. */
export function coachSwitchCopy(cap:Capability|null|undefined,permissions:readonly string[],enabled:boolean,loaded=true):CoachSwitchCopy{
 if(!loaded)return {available:false,tone:'neutral',state:'Checking',detail:'Checking whether live coach is available.'};
 if(!cap||cap.status==='unconfigured')return {available:false,tone:'warn',state:'Not set up',detail:'Live coach is not set up for this workspace, so no call content is sent anywhere.'};
 if(!cap.allowed||!permissions.includes('transcripts:read'))return {available:false,tone:'warn',state:'No access',detail:'Live coach needs transcript access on your account. Ask an admin to grant it.'};
 if(cap.status!=='enabled'&&cap.status!=='degraded')return {available:false,tone:'warn',state:'Off for workspace',detail:cap.detail||'Live coach is off for this workspace.'};
 return enabled?{available:true,tone:'live',state:'On',detail:'Tips appear on every call, and you can ask Coach questions.'}:{available:true,tone:'neutral',state:'Off',detail:'Turn on for live tips during calls.'};
}

/** A coach response as the contract allows it: at most three lines of at most COACH_LINE_MAX characters each (96 since
 * November 1, 2026, what two meta rows hold at 720 px; the API fits longer ones at a word boundary), or null. */
export function readCoachResponse(body:unknown):CoachResponse|null{
 const d=(body as {data?:CoachResponse}|null)?.data;
 if(!d||!Array.isArray(d.lines)||d.lines.length>3||!d.lines.every(s=>typeof s==='string'&&s.trim()!==''&&Array.from(s).length<=COACH_LINE_MAX)||
  (d.computed_at!==null&&(typeof d.computed_at!=='string'||!Number.isFinite(Date.parse(d.computed_at))))||!Number.isInteger(d.segments_used)||d.segments_used<0||
  !['anthropic','openai_compatible'].includes(d.provider)||typeof d.model!=='string'||typeof d.stale!=='boolean'||
  (d.retry_after_ms!==undefined&&(!Number.isInteger(d.retry_after_ms)||d.retry_after_ms<1||d.retry_after_ms>60000)))return null;
 return d;
}

export type CoachQaEntry=
 |{id:string;question:string;status:'loading'}
 |{id:string;question:string;status:'answered';answer:CoachAskAnswer}
 |{id:string;question:string;status:'error';error:ErrorView};

export type CoachQaAction=
 |{type:'asked';id:string;question:string}
 |{type:'answered';id:string;answer:CoachAskAnswer}
 |{type:'failed';id:string;error:ErrorView}
 |{type:'retried';id:string};

export function coachQaReducer(entries:readonly CoachQaEntry[],action:CoachQaAction):CoachQaEntry[]{
 switch(action.type){
  case 'asked':
   return [{id:action.id,question:action.question,status:'loading' as const},...entries].slice(0,ASK_HISTORY_LIMIT);
  case 'answered':
   return entries.map(e=>e.id===action.id?{id:e.id,question:e.question,status:'answered' as const,answer:action.answer}:e);
  case 'failed':
   return entries.map(e=>e.id===action.id?{id:e.id,question:e.question,status:'error' as const,error:action.error}:e);
  case 'retried':
   return entries.map(e=>e.id===action.id?{id:e.id,question:e.question,status:'loading' as const}:e);
 }
}

/** CoachAskAnswer.sources as the server sent them: only the contract's values, each once, in contract order. Undefined
 * when the reply has no list (an API from before the field), so the question thread falls back to what was sent. */
export function readAskSources(raw:unknown):AskSource[]|undefined{
 if(!Array.isArray(raw))return undefined;
 return ASK_SOURCES.filter(s=>raw.includes(s));
}

export function readCoachAskResponse(body:unknown):CoachAskAnswer|null{
 const d=(body as {data?:Record<string,unknown>}|null)?.data;
 if(!d||typeof d!=='object')return null;
 if(typeof d.suggestion!=='string'||!d.suggestion.trim()||d.suggestion.length>2000)return null;
 if(d.suggested_wording!==null&&d.suggested_wording!==undefined&&(typeof d.suggested_wording!=='string'||!d.suggested_wording.trim()||d.suggested_wording.length>2000))return null;
 if(d.customer_context!==null&&d.customer_context!==undefined&&(typeof d.customer_context!=='string'||!d.customer_context.trim()||d.customer_context.length>2000))return null;
 if(typeof d.model!=='string'||!d.model||!['anthropic','openai_compatible'].includes(String(d.provider)))return null;
 if(typeof d.latency_ms!=='number'||!Number.isInteger(d.latency_ms)||d.latency_ms<0)return null;
 if(typeof d.segments_used!=='number'||!Number.isInteger(d.segments_used)||d.segments_used<0)return null;
 if(typeof d.provisional!=='boolean'||!['answered','no_answer'].includes(String(d.state)))return null;
 const sources=readAskSources(d.sources);
 return {
  suggestion:d.suggestion,
  suggested_wording:typeof d.suggested_wording==='string'?d.suggested_wording:null,
  customer_context:typeof d.customer_context==='string'?d.customer_context:null,
  provider:d.provider as CoachAskAnswer['provider'],
  model:d.model,
  latency_ms:d.latency_ms,
  segments_used:d.segments_used,
  crm_card_used:d.crm_card_used===true,
  provisional:d.provisional,
  truncated:d.truncated===true,
  state:d.state as CoachAskAnswer['state'],
  ...(sources?{sources}:{}),
 };
}

export function coachGroundingLine(answer:Pick<CoachAskAnswer,'segments_used'|'crm_card_used'>):string{
 const parts:string[]=[];
 if(answer.segments_used>0)parts.push(`${answer.segments_used} transcript line${answer.segments_used===1?'':'s'}`);
 if(answer.crm_card_used)parts.push('CRM card');
 return parts.length?`Grounded in ${parts.join(' and ')}`:'Grounded in call context';
}

export function coachAnsweredLine(answer:Pick<CoachAskAnswer,'model'|'latency_ms'>):string{
 return `Coach suggestion from ${answer.model} in ${seconds(answer.latency_ms)} s`;
}

export function coachAskErrorView(error:unknown):ErrorView{
 const base=describeError(error,'Could not get a coaching suggestion');
 switch(base.code){
  case COACH_OFF_HOURS_CODE:
   return {...base,title:coachOffHoursLine(readCoachOffHours(error)?.until??null),detail:'Coach runs during working hours. Your call and transcript are unaffected.'};
  case 'coach_timeout':
  case 'ask_timeout':
   return {...base,title:'Coaching suggestion took too long',detail:'Try again or ask a shorter question.'};
  case 'coach_provider_error':
  case 'ask_provider_error':
   return {...base,title:'Coaching service could not answer',detail:'Try again in a moment. Your call and transcript are unaffected.'};
  case 'transcript_empty':
   return {...base,title:'No transcript or CRM context yet',detail:'Ask again once speech appears in the live transcript or a CRM card is attached.'};
  case 'coach_context_changed':
   return {...base,title:'Call context changed',detail:'Ask again using the current transcript and CRM card.'};
  case 'rate_limited':
   return {...base,title:'Too many questions in a minute',detail:'Wait a moment, then ask again.'};
  default:
   return base;
 }
}

export class CoachAskClient {
 constructor(private readonly api:Pick<ApiClient,'send'>){}
 ask(callId:string,question:string,signal?:AbortSignal):Promise<unknown>{
  return this.api.send('POST',`/calls/${encodeURIComponent(callId)}/coach`,{question:cleanQuestion(question)},signal?{signal}:{});
 }
}
/** The coach is outside its operating hours (a 503 coach_off_hours from GET or POST coach, before any provider call).
 * until: the ISO time it is back, when the server said. */
export interface CoachOffHours {until:string|null}
export interface CoachState {result:CoachResponse|null;thinking:boolean;error:string|null;offHours?:CoachOffHours|null}
const initial=():CoachState=>({result:null,thinking:false,error:null});
/** While the coach is off hours the window asks again at most this often (the 503 is answered before any provider
 * call), and never sooner than the normal poll. */
export const COACH_OFF_HOURS_RECHECK_MS=600000;
/** The bench's tip timer (docs/design/CALL-POP-KRISP-BAR.md section 9): from the prospect's turn end (their final) to
 * the fresh tip arriving for the say-next slot, as a performance measure under this name. */
export const COACH_TIP_LATENCY_MEASURE='sidetone:tip-latency';

/**
 * Reads a coach_off_hours refusal: until and retry_after_ms from the error itself, or from its fields (the API's
 * error envelope keeps string fields). Without until, retry_after_ms is counted from this browser's clock. Null for
 * any other error.
 */
export function readCoachOffHours(error:unknown,wallNow:number=Date.now()):{until:string|null;recheckMs:number}|null{
 if(!error||typeof error!=='object')return null;
 const e=error as {code?:unknown;until?:unknown;retry_after_ms?:unknown;retryAfterMs?:unknown;fields?:unknown};
 if(e.code!==COACH_OFF_HOURS_CODE)return null;
 const fields=e.fields&&typeof e.fields==='object'?e.fields as Record<string,unknown>:{};
 const rawRetry=e.retry_after_ms??e.retryAfterMs??fields.retry_after_ms;
 const retry=typeof rawRetry==='number'?rawRetry:typeof rawRetry==='string'&&/^\d{1,10}$/.test(rawRetry)?Number(rawRetry):NaN;
 const retryMs=Number.isInteger(retry)&&retry>0?retry:null;
 const rawUntil=e.until??fields.until;
 let until=typeof rawUntil==='string'&&rawUntil.length<=40&&Number.isFinite(Date.parse(rawUntil))?rawUntil:null;
 if(!until&&retryMs!==null)until=new Date(wallNow+retryMs).toISOString();
 return {until,recheckMs:Math.min(COACH_OFF_HOURS_RECHECK_MS,Math.max(COACH_POLL_MS,retryMs??COACH_OFF_HOURS_RECHECK_MS))};
}

/** "Coach is off until 7:00": the local time the coach is back, on this browser's clock; the full weekday too when that
 * is more than a day away ("Coach is off until Monday 7:00" from Friday evening: COACH_DAYS closes the weekend). */
export function coachOffHoursLine(until:string|null,now:number=Date.now()):string{
 const at=until?Date.parse(until):NaN;
 if(!Number.isFinite(at))return 'Coach is off for now';
 const {day,time}=coachOpening(at,now);
 return `Coach is off until ${day?day+' ':''}${time}`;
}

/**
 * The call window's trigger (docs/design/CALL-POP-KRISP-BAR.md lane 2): a tip is asked for when the prospect's turn
 * ends, that is a final from anyone who is not me. My own finals and corrections never ask; a correction of someone
 * else's words dims the tip (stale) instead of clearing it, a correction of my own words leaves it as it is (they say
 * nothing new about the prospect, and the ASR refines many finals right after they land, so the tip the rep is reading
 * aloud would grey out mid-sentence), an empty "not yet" answer keeps the dimmed tip until the next one replaces it, and
 * there is no periodic poll: failures and off hours schedule their own retry.
 */
export interface CoachTurnTrigger {isSelf(participantId:string|null):boolean}

/** One non-overlapping request per poll; stop cancels and ignores even a late success from an old call/access epoch. */
export class CoachPoller {
 private state:CoachState=initial();
 private listeners=new Set<()=>void>();
 private timer:ReturnType<typeof setInterval>|null=null;
 private controller:AbortController|null=null;
 private generation=0;
 private unsubscribeAccess:(()=>void)|null=null;
 private unsubscribeStream:(()=>void)|null=null;
 private retryTimer:ReturnType<typeof setTimeout>|null=null;
 constructor(private readonly api:Pick<ApiClient,'request'>,private readonly access?:MediaAccess,
  private readonly stream:(fn:(notification:TranscriptStreamNotification)=>void)=>()=>void=subscribeTranscriptStream,
  private readonly now:()=>number=()=>performance.now()){}
 snapshot=()=>this.state;
 subscribe=(fn:()=>void)=>{this.listeners.add(fn);return ()=>{this.listeners.delete(fn);};};
 private update(next:CoachState){this.state=next;for(const fn of this.listeners)fn();}
 start(call:Pick<Call,'id'|'state'>,enabled:boolean,trigger?:CoachTurnTrigger){
  this.stop();
  if(call.state!=='active'||!enabled||this.access&&this.access.getSnapshot().state!=='ready')return;
  const generation=this.generation;
  const epoch=this.access?.getSnapshot().epoch;
  let lastAttempt=-Infinity,notBefore=0,queued=false,retryAt=Infinity;
  /** The earliest prospect turn end not yet answered by a fresh tip (the bench's tip timer). */
  let turnEndedAt:number|null=null;
  const dim=()=>{const r=this.state.result;if(r&&r.lines.length&&!r.stale)this.update({...this.state,result:{...r,stale:true}});};
  const seen=new Map<string,{revision:number;final:boolean}>();
  const clearRetry=()=>{if(this.retryTimer!==null)clearTimeout(this.retryTimer);this.retryTimer=null;retryAt=Infinity;};
  const schedule=(delay=0)=>{
   if(generation!==this.generation)return;
   const wait=Math.max(delay,notBefore-this.now(),lastAttempt+COACH_REQUEST_GAP_MS-this.now(),0);
   const deadline=this.now()+Math.ceil(wait);
   if(this.retryTimer!==null&&retryAt<=deadline)return;
   clearRetry();retryAt=deadline;
   this.retryTimer=setTimeout(()=>{this.retryTimer=null;retryAt=Infinity;void poll();},Math.ceil(wait));
  };
  this.unsubscribeAccess=this.access?.subscribe(()=>{
   if(this.access?.getSnapshot().epoch!==epoch)this.stop();
  })??null;
  const poll=async()=>{
   if(generation!==this.generation)return;
   if(this.controller){queued=true;return;}
   if(this.now()<Math.max(notBefore,lastAttempt+COACH_REQUEST_GAP_MS)){schedule();return;}
   clearRetry();queued=false;lastAttempt=this.now();
   const controller=new AbortController();this.controller=controller;
   this.update({...this.state,thinking:true,error:null});
   try{
    const body=await this.api.request<DataEnvelope<CoachResponse>>('/calls/'+encodeURIComponent(call.id)+'/coach',{signal:controller.signal});
    if(controller.signal.aborted||generation!==this.generation)return;
    const result=readCoachResponse(body);
    if(!result)throw new Error('invalid_coach_response');
    // The hint is a duration, anchored on receipt in this browser's monotonic clock.
    // Never compare computed_at (a server timestamp) to the browser wall clock.
    notBefore=result.retry_after_ms===undefined?0:this.now()+result.retry_after_ms;
    if(trigger){
     // "Not yet" (no lines, stale) never blanks the slot: the last tip stays, dimmed, until the next one replaces it.
     const previous=this.state.result;
     const shown=!result.lines.length&&result.stale&&previous&&previous.lines.length?{...previous,stale:true}:result;
     this.update({result:shown,thinking:false,error:null,offHours:null});
     if(result.lines.length&&!result.stale&&turnEndedAt!==null){measureTipLatency(turnEndedAt,this.now());turnEndedAt=null;}
    }else this.update({result,thinking:false,error:null});
    if(result.stale&&result.retry_after_ms!==undefined)queued=true;
   }catch(error){
    if(controller.signal.aborted||generation!==this.generation||isAbortError(error))return;
    const off=trigger?readCoachOffHours(error):null;
    if(off){
     // Off hours: the tip on screen stays dimmed, the status says when the coach is back, and the window asks again
     // later on its own (the server refuses before any provider call).
     const r=this.state.result;
     this.update({result:r?{...r,stale:true}:null,thinking:false,error:null,offHours:{until:off.until}});
     notBefore=this.now()+off.recheckMs;queued=true;
     return;
    }
    // Temporary provider/network failures keep the last guidance visibly stale.
    // Authorization failures still clear all derived content immediately.
    const status=(error as {status?:number}).status;
    const result=status===401||status===403||status===404||status===409||status===410?null:this.state.result?{...this.state.result,stale:true}:null;
    this.update({result,thinking:false,error:'Coach is unavailable. It will try again shortly.'});
    notBefore=this.now()+COACH_POLL_MS;
    // With no periodic poll the retry is scheduled here.
    if(trigger)queued=true;
   }finally{
    if(this.controller===controller){this.controller=null;if(queued&&generation===this.generation)schedule();}
   }
  };
  this.unsubscribeStream=this.stream(notification=>{
   if(generation!==this.generation)return;
   if(notification.kind==='event'){
    const event=notification.event;
    if(event.type==='transcript.state_changed'&&event.aggregate.id===call.id){
     // Withdrawal/resync must not leave old derived advice on screen.
     this.controller?.abort();this.controller=null;this.update(initial());
    }else{
     const data=event.data as {call_id?:unknown;segment?:unknown}|undefined;
     if(event.aggregate.type!=='transcript_segment'||data?.call_id!==call.id)return;
     const segment=readTranscriptSegment(data.segment);
     if(!segment)return;
     const previous=seen.get(segment.id);
     if(previous&&previous.revision>=segment.revision)return;
     seen.set(segment.id,{revision:segment.revision,final:segment.is_final});
     if(seen.size>4000)seen.delete(seen.keys().next().value!);
     if(trigger){
      // A correction (a newer revision of a final, or a refined final) goes to the server's transcript, not to a
      // request: the tip stays, dimmed. Partials and my own finals never ask; anyone else's final ends a turn.
      const correction=previous?.final===true||event.type==='transcript.corrected'||!previous&&segment.is_final&&segment.source==='refined';
      if(correction){if(!trigger.isSelf(segment.participant_id))dim();return;}
      if(!segment.is_final||trigger.isSelf(segment.participant_id))return;
      turnEndedAt??=this.now();
     }else{
      // The ASR's refined correction (lane 1: the whole-window decode of a final it already published, the same speech
      // heard again) withdraws nothing: the tip stays on screen, dimmed, no request is aborted and none is added; the
      // next poll reads the corrected transcript. It follows a large share of finals, so blanking and asking again on
      // each one would flash the coach and double its provider calls (docs/design/CALL-POP-KRISP-BAR.md section 0).
      if(event.type==='transcript.corrected'&&segment.is_final&&segment.source==='refined'){dim();return;}
      // A known provisional -> first final is appended speech. A newer revision
      // of a final (or an unknown revised segment after reconnect) may withdraw input.
      const invalidates=previous?.final===true||!previous&&segment.revision>1;
      if(invalidates){this.controller?.abort();this.controller=null;this.update(initial());}
      if(!segment.is_final&&!invalidates)return;
     }
    }
   }else{
    this.controller?.abort();this.controller=null;this.update(initial());
   }
   queued=true;
   if(!this.controller)schedule(COACH_EVENT_DEBOUNCE_MS);
  });
  void poll();
  if(!trigger)this.timer=setInterval(()=>{void poll();},COACH_POLL_MS);
 }
 stop(){
  ++this.generation;
  this.unsubscribeAccess?.();this.unsubscribeAccess=null;
  this.unsubscribeStream?.();this.unsubscribeStream=null;
  if(this.retryTimer!==null)clearTimeout(this.retryTimer);this.retryTimer=null;
  if(this.timer!==null)clearInterval(this.timer);this.timer=null;
  this.controller?.abort();this.controller=null;
  this.update(initial());
 }
}

function measureTipLatency(start:number,end:number){
 try{globalThis.performance?.measure?.(COACH_TIP_LATENCY_MEASURE,{start,end});}catch{/* No User Timing here: the tip is shown all the same. */}
}
